import { useEffect, useMemo, useRef, useState } from "react";
import AutoGrowTextarea from "./auto-grow-textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useAuthContext } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Sparkles, PhoneCall, PenLine, Send, Loader2, CheckCircle2, Download,
} from "lucide-react";
import { format } from "date-fns";
import type { Sponsor, CnaAnalysis } from "@shared/schema";

/**
 * Document-style CNA editor. Fields are open, auto-growing writing surfaces
 * (never fixed-height boxes with inner scrollbars), laid out in a single
 * reading column per section, with a sticky phase rail for orientation on
 * wide screens and a floating save bar.
 */

interface FieldDef {
  key: string;
  label: string;
  hint?: string;
  placeholder?: string;
  input?: "textarea" | "date" | "select";
  options?: string[]; // for input: "select"
  ai?: boolean; // filled by "Research with AI"
  required?: boolean; // must be filled before PDF export
}

const POINT1_FIELDS: FieldDef[] = [
  { key: "productServiceDescription", label: "Product / Service Description", ai: true, required: true, placeholder: "What does this company offer?" },
  { key: "uniqueValueProposition", label: "Company's Unique Value Proposition", ai: true, required: true, placeholder: "What makes them different?" },
  { key: "idealCustomer", label: "Company's Ideal Customer", ai: true, required: true, placeholder: "Who are they trying to reach?" },
  { key: "customerGeography", label: "Where Customers Come From Geographically", ai: true, required: true, placeholder: "Cities, states, national, etc." },
  { key: "assumedCompetitors", label: "Assumed Competitors", ai: true, required: true, placeholder: "Who are they up against?" },
  { key: "assumedMarketingChallenges", label: "Assumed Marketing Challenges", ai: true, required: true, placeholder: "What likely keeps their marketing team up at night?" },
  { key: "assumedMarketingGoals", label: "Assumed Marketing Goals", ai: true, required: true, placeholder: "What are they probably trying to achieve this year?" },
  { key: "companyNews", label: "Company News", ai: true, placeholder: "Anything recent worth mentioning on the call?" },
];

const DURING_FIELDS: FieldDef[] = [
  { key: "campaignTimeline", label: "Campaign Timeline / Duration", hint: "Derive during the call.", required: true, placeholder: "When do they want to start and for how long?" },
  { key: "budgetRange", label: "Budget Range", hint: "Derive during the call.", required: true, placeholder: "What budget range are they working with?" },
  { key: "currentMedia", label: "Current Media / Marketing", placeholder: "What mediums or platforms are they using?" },
  { key: "currentMediaNotes", label: "Notes on Current Media / Marketing", placeholder: "What is working? What is not?" },
  { key: "audienceNotReached", label: "Audience Not Reached by Current Media / Marketing", placeholder: "Who are they missing?" },
  { key: "reconnectDate", label: "Date to Reconnect on Partnership Proposal", input: "date" },
];

const AFTER_FIELDS: FieldDef[] = [
  { key: "whyPublicMedia", label: "Why Public Media is a Solution", required: true, placeholder: "Connect our audiences and halo to their goals, or draft it with AI." },
  { key: "resonatingInfo", label: "Information / Data Shared Previously That Resonated", placeholder: "What landed with them so far?" },
  { key: "campaignDuration", label: "Campaign Duration", required: true, placeholder: "Recommended flight length" },
  { key: "suggestedPlatforms", label: "Suggested Platforms", required: true, placeholder: "Broadcast, streaming, podcasts, events..." },
  { key: "stationsToInclude", label: "Stations to Include", required: true, placeholder: "Which partner stations fit this sponsor?" },
  { key: "recommendedCopy", label: "Recommended Broadcast Copy", placeholder: "Use AI Copy Engine as needed." },
];

const HANDOFF_FIELDS: FieldDef[] = [
  { key: "proposalContacts", label: "Prepare Proposal For: (Contact Name(s) and Title(s))", required: true, placeholder: "Who receives the proposal?" },
  { key: "sponsorStatus", label: "Sponsor Status", input: "select", required: true, placeholder: "Select status", options: ["New Business", "Recovery", "Retention", "Upsell"] },
  { key: "additionalNotes", label: "Additional Notes", placeholder: "Anything else the Solution Strategist should know" },
];

const ALL_FIELDS = [...POINT1_FIELDS, ...DURING_FIELDS, ...AFTER_FIELDS, ...HANDOFF_FIELDS];
const ALL_KEYS = ALL_FIELDS.map((f) => f.key);
const REQUIRED_FIELDS = ALL_FIELDS.filter((f) => f.required);

type FormState = Record<string, string>;

function formFromCna(cna: CnaAnalysis | null): FormState {
  const out: FormState = {};
  for (const key of ALL_KEYS) out[key] = String((cna as any)?.[key] ?? "");
  return out;
}

function filledCount(form: FormState, fields: FieldDef[]): number {
  return fields.filter((f) => form[f.key]?.trim()).length;
}

interface PhaseDef {
  id: string;
  label: string;
  icon: typeof Sparkles;
  fields: FieldDef[];
  accent: string; // left accent bar
  iconWrap: string; // tinted icon circle
}

const PHASES: PhaseDef[] = [
  { id: "before", label: "Before the call", icon: Sparkles, fields: POINT1_FIELDS, accent: "bg-purple-500", iconWrap: "bg-purple-100 text-purple-600" },
  { id: "during", label: "During the call", icon: PhoneCall, fields: DURING_FIELDS, accent: "bg-blue-500", iconWrap: "bg-blue-100 text-blue-600" },
  { id: "after", label: "After the call", icon: PenLine, fields: AFTER_FIELDS, accent: "bg-emerald-500", iconWrap: "bg-emerald-100 text-emerald-600" },
  { id: "handoff", label: "Handoff", icon: Send, fields: HANDOFF_FIELDS, accent: "bg-orange-400", iconWrap: "bg-orange-100 text-orange-500" },
];

interface CnaEditorProps {
  sponsor: Sponsor;
  cnas: CnaAnalysis[];
}

export default function CnaEditor({ sponsor, cnas }: CnaEditorProps) {
  const { toast } = useToast();
  const { getAuthToken } = useAuthContext();
  const activityKey = `/api/sponsors/${sponsor.id}/activity`;

  const [activeCnaId, setActiveCnaId] = useState<string | null>(cnas[0]?.id ?? null);
  const activeCna = useMemo(
    () => cnas.find((c) => c.id === activeCnaId) ?? null,
    [cnas, activeCnaId],
  );

  const [form, setForm] = useState<FormState>(() => formFromCna(activeCna));
  const [snapshot, setSnapshot] = useState<FormState>(() => formFromCna(activeCna));
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [aiMeta, setAiMeta] = useState<any>(activeCna?.aiMetadata ?? null);
  const [exporting, setExporting] = useState(false);
  // Paint missing required fields only after an export attempt, so the form
  // does not open covered in red.
  const [showMissing, setShowMissing] = useState(false);

  // Resync only when switching documents, never on background refetch, so
  // in-progress edits are never clobbered.
  useEffect(() => {
    const next = formFromCna(activeCna);
    setForm(next);
    setSnapshot(next);
    setAiMeta(activeCna?.aiMetadata ?? null);
    setLastSavedAt(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCnaId]);

  useEffect(() => {
    if (!activeCnaId && cnas.length > 0) setActiveCnaId(cnas[0].id);
    if (activeCnaId && !cnas.some((c) => c.id === activeCnaId)) setActiveCnaId(cnas[0]?.id ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cnas]);

  // First open: create the CNA automatically so the user lands directly in
  // the form (no "Start CNA" step). Ref-guarded so strict-mode re-renders
  // and refetches cannot fire a second create.
  const autoCreatedRef = useRef(false);
  useEffect(() => {
    if (cnas.length === 0 && !autoCreatedRef.current && !createCna.isPending) {
      autoCreatedRef.current = true;
      createCna.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cnas.length]);

  const dirty = JSON.stringify(form) !== JSON.stringify(snapshot);
  const missingRequired = REQUIRED_FIELDS.filter((f) => !form[f.key]?.trim());
  const set = (key: string, value: string) => setForm((prev) => ({ ...prev, [key]: value }));

  const createCna = useMutation({
    mutationFn: async () => apiRequest(`/api/sponsors/${sponsor.id}/cnas`, "POST", {}),
    onSuccess: (cna: CnaAnalysis) => {
      queryClient.invalidateQueries({ queryKey: [activityKey] });
      setActiveCnaId(cna.id);
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message || "Failed to start CNA", variant: "destructive" });
    },
  });

  // Race-safe save: the PATCH body and the post-save snapshot both come from
  // the form value PASSED to mutate, not the latest render. Keystrokes made
  // while a save is in flight therefore stay "dirty" and autosave again.
  const saveCna = useMutation({
    mutationFn: async (formToSave: FormState) => {
      if (!activeCna) return;
      const body: Record<string, string | null> = {};
      for (const key of ALL_KEYS) body[key] = formToSave[key].trim() ? formToSave[key] : null;
      return apiRequest(`/api/cnas/${activeCna.id}`, "PATCH", body);
    },
    onSuccess: (_data, formToSave) => {
      setSnapshot({ ...formToSave });
      setLastSavedAt(new Date());
      queryClient.invalidateQueries({ queryKey: [activityKey] });
    },
    onError: (error: Error) => {
      toast({ title: "Save failed", description: error.message || "Could not save the CNA", variant: "destructive" });
    },
  });

  // Autosave: persist edits ~1.5s after the user stops typing. Re-arms after
  // an in-flight save settles if further edits arrived meanwhile.
  useEffect(() => {
    if (!dirty || !activeCna || saveCna.isPending) return;
    const timer = setTimeout(() => saveCna.mutate(form), 1500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, dirty, saveCna.isPending, activeCnaId]);

  const researchPoint1 = useMutation({
    mutationFn: async () => apiRequest(`/api/cnas/${activeCna!.id}/generate-point1`, "POST", {}),
    onSuccess: (updated: CnaAnalysis) => {
      const merge = (prev: FormState) => {
        const next = { ...prev };
        for (const f of POINT1_FIELDS) next[f.key] = String((updated as any)[f.key] ?? "");
        return next;
      };
      setForm(merge);
      setSnapshot(merge);
      setAiMeta(updated.aiMetadata ?? null);
      queryClient.invalidateQueries({ queryKey: [activityKey] });
      toast({ title: "Research complete", description: "Section 1 was filled from the company's website and recent news. Review and edit freely." });
    },
    onError: (error: Error) => {
      toast({ title: "Research failed", description: error.message || "Could not research this company", variant: "destructive" });
    },
  });

  const draftWhy = useMutation({
    mutationFn: async () => apiRequest(`/api/cnas/${activeCna!.id}/draft-why-public-media`, "POST", {}),
    onSuccess: (updated: CnaAnalysis) => {
      const merge = (prev: FormState) => ({ ...prev, whyPublicMedia: String(updated.whyPublicMedia ?? "") });
      setForm(merge);
      setSnapshot(merge);
      queryClient.invalidateQueries({ queryKey: [activityKey] });
    },
    onError: (error: Error) => {
      toast({ title: "Draft failed", description: error.message || "Could not draft this section", variant: "destructive" });
    },
  });

  // Required fields must be complete before export; then save (if dirty) so
  // the PDF always matches the screen, then download via authenticated fetch.
  const exportPdf = async () => {
    if (!activeCna) return;
    if (missingRequired.length > 0) {
      setShowMissing(true);
      toast({
        title: "Required fields missing",
        description: `Complete the required fields before exporting the PDF - ${missingRequired.length} remaining.`,
        variant: "destructive",
      });
      document.getElementById(`cna-${missingRequired[0].key}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setExporting(true);
    try {
      if (dirty) await saveCna.mutateAsync(form);
      const token = await getAuthToken();
      const headers: HeadersInit = {};
      if (token) headers["Authorization"] = `Bearer ${token}`;
      const response = await fetch(`/api/cnas/${activeCna.id}/pdf`, { headers, credentials: "include" });
      if (!response.ok) throw new Error("Export failed");
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `CNA - ${sponsor.name} - ${format(new Date(), "yyyy-MM-dd")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast({ title: "Export failed", description: (err as Error).message || "Could not export the PDF", variant: "destructive" });
    } finally {
      setExporting(false);
    }
  };

  const scrollToPhase = (id: string) => {
    document.getElementById(`cna-section-${id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // The first CNA is auto-created (effect above); show skeletons while it
  // lands, or a retry if the create failed.
  if (!activeCna) {
    if (createCna.isError) {
      return (
        <div className="text-center py-14 border border-dashed rounded-xl">
          <p className="text-sm text-gray-500 mb-3">Could not start the CNA for {sponsor.name}.</p>
          <Button onClick={() => createCna.mutate()} disabled={createCna.isPending} data-testid="button-start-cna">
            {createCna.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
            Try again
          </Button>
        </div>
      );
    }
    return (
      <div className="space-y-6">
        <div className="flex justify-end gap-2">
          <Skeleton className="h-9 w-28 rounded-md" />
          <Skeleton className="h-9 w-24 rounded-md" />
        </div>
        <Skeleton className="h-72 w-full rounded-xl" />
        <Skeleton className="h-48 w-full rounded-xl" />
      </div>
    );
  }

  const renderField = (f: FieldDef) => {
    const missing = showMissing && !!f.required && !form[f.key]?.trim();
    return (
      <div key={f.key} className="group">
        <div className="flex items-center gap-2 mb-0.5">
          <label htmlFor={`cna-${f.key}`} className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">
            {f.label}
            {f.required && <span className="text-red-400 ml-0.5 normal-case">*</span>}
          </label>
          {f.ai && form[f.key]?.trim() && aiMeta && (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-purple-500 border-purple-200">
              <Sparkles className="h-2.5 w-2.5 mr-0.5" /> AI
            </Badge>
          )}
        </div>
        {f.input === "date" ? (
          <Input
            id={`cna-${f.key}`}
            type="date"
            value={form[f.key]}
            onChange={(e) => set(f.key, e.target.value)}
            className={`max-w-[220px] border-0 border-b rounded-none px-0 focus-visible:ring-0 focus-visible:border-purple-400 ${missing ? "border-red-300" : "border-gray-100"}`}
            data-testid={`input-cna-${f.key}`}
          />
        ) : f.input === "select" ? (
          <Select value={form[f.key] || undefined} onValueChange={(v) => set(f.key, v)}>
            <SelectTrigger
              id={`cna-${f.key}`}
              className={`max-w-[280px] border-0 border-b rounded-none px-0 shadow-none focus:ring-0 text-[15px] ${missing ? "border-red-300" : "border-gray-100"} ${form[f.key] ? "text-gray-900" : "text-gray-300"}`}
              data-testid={`input-cna-${f.key}`}
            >
              <SelectValue placeholder={f.placeholder ?? "Select"} />
            </SelectTrigger>
            <SelectContent>
              {(f.options ?? []).map((opt) => (
                <SelectItem key={opt} value={opt}>{opt}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <AutoGrowTextarea
            id={`cna-${f.key}`}
            value={form[f.key]}
            onChange={(v) => set(f.key, v)}
            placeholder={f.placeholder}
            invalid={missing}
            testId={`input-cna-${f.key}`}
          />
        )}
        {missing && <p className="text-xs text-red-400 mt-1">Required for PDF export</p>}
        {f.hint && <p className="text-xs text-gray-300 mt-1 group-focus-within:text-gray-400">{f.hint}</p>}
      </div>
    );
  };

  const sectionHeader = (phase: PhaseDef, title: string, subtitle: string, action?: React.ReactNode) => (
    <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
      <div className="flex items-start gap-3">
        <div className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 ${phase.iconWrap}`}>
          <phase.icon className="h-[18px] w-[18px]" />
        </div>
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">{phase.label}</p>
          <h3 className="text-lg font-semibold text-gray-900 leading-snug">{title}</h3>
          <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>
        </div>
      </div>
      {action}
    </div>
  );

  const sectionShell = (phase: PhaseDef, children: React.ReactNode) => (
    <section
      id={`cna-section-${phase.id}`}
      className="relative overflow-hidden rounded-xl bg-white shadow-sm border border-gray-100 p-6 sm:p-8 scroll-mt-24"
    >
      <div className={`absolute left-0 top-0 bottom-0 w-1 ${phase.accent}`} />
      {children}
    </section>
  );

  return (
    <div className="xl:grid xl:grid-cols-[230px_1fr] xl:gap-8">
      {/* Phase rail (wide screens) */}
      <aside className="hidden xl:block">
        <div className="sticky top-24 space-y-1">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 px-3 pb-1">Workflow</p>
          {PHASES.map((phase) => {
            const done = filledCount(form, phase.fields);
            const complete = done === phase.fields.length;
            return (
              <button
                key={phase.id}
                onClick={() => scrollToPhase(phase.id)}
                className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-gray-600 hover:bg-white hover:shadow-sm transition-all text-left"
                data-testid={`rail-${phase.id}`}
              >
                <span className={`h-6 w-6 rounded-full flex items-center justify-center shrink-0 ${complete ? "bg-green-100 text-green-600" : phase.iconWrap}`}>
                  {complete ? <CheckCircle2 className="h-3.5 w-3.5" /> : <phase.icon className="h-3.5 w-3.5" />}
                </span>
                <span className="flex-1 truncate">{phase.label}</span>
                <span className={`text-xs ${complete ? "text-green-600" : "text-gray-400"}`}>{done}/{phase.fields.length}</span>
              </button>
            );
          })}
        </div>
      </aside>

      {/* Document */}
      <div className="space-y-6 pb-24 min-w-0">
        {/* Toolbar */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          {/* Compact phase chips (small screens only) */}
          <div className="flex flex-wrap gap-2 xl:hidden">
            {PHASES.map((phase) => {
              const done = filledCount(form, phase.fields);
              const complete = done === phase.fields.length;
              return (
                <button
                  key={phase.id}
                  onClick={() => scrollToPhase(phase.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border transition-colors ${
                    complete ? "bg-green-50 text-green-700 border-green-200" : "bg-white text-gray-600 border-gray-200"
                  }`}
                >
                  {complete ? <CheckCircle2 className="h-3.5 w-3.5" /> : <phase.icon className="h-3.5 w-3.5" />}
                  {phase.label}
                  <span className={complete ? "text-green-600" : "text-gray-400"}>{done}/{phase.fields.length}</span>
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-2 ml-auto">
            {cnas.length > 1 && (
              <Select value={activeCna.id} onValueChange={setActiveCnaId}>
                <SelectTrigger className="w-[200px]" data-testid="select-cna-version">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {cnas.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      CNA from {c.createdAt ? format(new Date(c.createdAt), "MMM d, yyyy") : "unknown date"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Button variant="outline" size="sm" onClick={exportPdf} disabled={exporting} data-testid="button-export-cna-pdf">
              {exporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
              {exporting ? "Exporting..." : "Export PDF"}
            </Button>
            <Button variant="outline" size="sm" onClick={() => createCna.mutate()} disabled={createCna.isPending} data-testid="button-new-cna">
              New CNA
            </Button>
          </div>
        </div>

        {/* Before the call: Point 1 */}
        {sectionShell(PHASES[0], (
          <>
            {sectionHeader(
              PHASES[0],
              "Section 1: Company's Interests & Marketing Objectives",
              "Review the AI research before the meeting; verify it during the call.",
              <Button
                onClick={() => researchPoint1.mutate()}
                disabled={researchPoint1.isPending}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="button-research-point1"
              >
                {researchPoint1.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                {researchPoint1.isPending ? "Researching..." : "Research with AI"}
              </Button>,
            )}
            {researchPoint1.isPending && (
              <p className="text-xs text-purple-600 -mt-4 mb-4">Reading {sponsor.website || "the web"} and recent news. This can take up to a minute.</p>
            )}
            {aiMeta?.generatedAt && !researchPoint1.isPending && (
              <p className="text-xs text-gray-400 -mt-4 mb-4">
                AI research from {format(new Date(aiMeta.generatedAt), "MMM d, yyyy h:mm a")} ({aiMeta.model}). Everything is editable.
              </p>
            )}
            <div className="max-w-3xl space-y-6">
              {POINT1_FIELDS.map(renderField)}
            </div>
          </>
        ))}

        {/* During the call: Point 2 */}
        {sectionShell(PHASES[1], (
          <>
            {sectionHeader(
              PHASES[1],
              "Section 2: Marketing Evaluation",
              "Derive the campaign timeline, budget range, and the company's current media and marketing mix on the call.",
            )}
            <div className="max-w-3xl space-y-6">
              {DURING_FIELDS.map(renderField)}
            </div>
          </>
        ))}

        {/* After the call: Points 3 & 4 */}
        {sectionShell(PHASES[2], (
          <>
            {sectionHeader(
              PHASES[2],
              "Sections 3 & 4: Why Public Media + Recommendation",
              "Post-call: connect our assets to their objectives, then strategize the recommendation.",
              <Button
                variant="outline"
                onClick={() => draftWhy.mutate()}
                disabled={draftWhy.isPending}
                data-testid="button-draft-why-public-media"
              >
                {draftWhy.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                {draftWhy.isPending ? "Drafting..." : "Draft 'Why Public Media' with AI"}
              </Button>,
            )}
            <div className="max-w-3xl space-y-6">
              {AFTER_FIELDS.map(renderField)}
            </div>
          </>
        ))}

        {/* Handoff */}
        {sectionShell(PHASES[3], (
          <>
            {sectionHeader(
              PHASES[3],
              "Notes for Internal Handoff",
              "Post-call: additional information for the Solution Strategist.",
            )}
            <div className="max-w-3xl space-y-6">
              {HANDOFF_FIELDS.map(renderField)}
            </div>
          </>
        ))}

        {/* Floating autosave status */}
        <div className="sticky bottom-4 z-10 flex justify-center">
          <div className="flex items-center gap-2 rounded-full bg-white shadow-lg border border-gray-200 px-5 py-2.5 text-sm text-gray-500" data-testid="cna-autosave-status">
            {saveCna.isPending ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Saving...
              </>
            ) : dirty ? (
              <>
                <span className="h-2 w-2 rounded-full bg-amber-400 inline-block" /> Unsaved changes
              </>
            ) : lastSavedAt ? (
              <>
                <CheckCircle2 className="h-3.5 w-3.5 text-green-500" /> Saved at {format(lastSavedAt, "h:mm a")}
              </>
            ) : (
              "All changes saved"
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
